import React, { useState, useMemo, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fields, generateTimeSlots, getDisplayPrice } from '../../data/mockData';
import { AMENITY_LABELS, Amenities, DayKey } from '../../types';
import { getCustomerById, getSession } from '../../data/authStore';
import {
  approveSubscriptionByOwner,
  cancelSubscription,
  cancelSubscriptionByOwner,
  createMonthlySubscription,
  getFieldSubscriptionsForOwner,
  getOrCreateSubscriberId,
  getSubscriberCount,
  getSubscriberSubscriptionsAll,
  isSlotBlockedBySubscription,
  isSlotOwnedBySubscriber,
  markSubscriptionPaid,
} from '../../data/subscriptionStore';
import { getFieldRatingSummary, getReviewsByField, upsertFieldReview } from '../../data/reviewStore';
import ImageGallery from '../../components/ImageGallery';
import FavoriteButton from '../../components/FavoriteButton';

const MapView = lazy(() => import('../../components/MapView'));

const FieldDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const field = fields.find(f => f.id === id);
  const session = getSession();
  const customer = session?.role === 'customer' ? getCustomerById(session.userId) : undefined;
  const customerSessionUserId = session?.role === 'customer' ? session.userId : '';

  const [, setSubscriptionRefresh] = useState(0);
  const [reviewRefresh, setReviewRefresh] = useState(0);
  const [reviewError, setReviewError] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [subscriptionError, setSubscriptionError] = useState('');
  const [subscriptionSuccess, setSubscriptionSuccess] = useState('');
  const subscriberId = useMemo(
    () => (session?.role === 'customer' ? `cust-${customerSessionUserId}` : getOrCreateSubscriberId()),
    [session?.role, customerSessionUserId]
  );
  const [subscriptionDay, setSubscriptionDay] = useState<DayKey>('mon');
  const [subscriptionTimeLabel, setSubscriptionTimeLabel] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });

  const weekDates = useMemo(() => {
    const dates: { date: string; label: string; dayName: string }[] = [];
    const dayNames = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      dates.push({
        date: dateStr,
        label: `${d.getDate()} ${['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'][d.getMonth()]}`,
        dayName: dayNames[d.getDay()],
      });
    }
    return dates;
  }, []);

  const timeSlots = useMemo(() => {
    if (!field) return [];
    return generateTimeSlots(field.id, selectedDate);
  }, [field, selectedDate]);

  const formatHourValue = (value: number): string => {
    const totalMinutes = Math.round(value * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  if (!field) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-gray-900 text-2xl font-bold mb-4">Saha Bulunamadi</h2>
          <button onClick={() => navigate('/sahalar')} className="text-primary-600 hover:text-primary-700 font-medium">
            Sahalara Don
          </button>
        </div>
      </div>
    );
  }

  const amenityKeys = Object.keys(AMENITY_LABELS) as (keyof Amenities)[];
  const priorityAmenityKeys: (keyof Amenities)[] = [
    'prayerRoom',
    'toilet',
    'hotWater',
    'parking',
    'shower',
    'lighting',
  ];
  const sizeOptionsLabel = (field.sizeOptions && field.sizeOptions.length > 0 ? field.sizeOptions : [field.size]).join(', ');
  const dayKeys: Array<'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat'> = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const selectedDayKey = dayKeys[new Date(selectedDate).getDay()] || 'mon';
  const selectedDayRange = field.availableTimeRangesByDay?.[selectedDayKey];
  const subscriberCount = getSubscriberCount(field.id);
  const displayPrice = getDisplayPrice(field);
  const mySubscriptions = getSubscriberSubscriptionsAll(field.id, subscriberId);
  const fieldSubscriptions = getFieldSubscriptionsForOwner(field.id);
  const isOwnerOfField = session?.role === 'owner' && session.ownerId === field.ownerId;
  const dayLabels: Record<DayKey, string> = {
    mon: 'Pazartesi',
    tue: 'Salı',
    wed: 'Çarşamba',
    thu: 'Perşembe',
    fri: 'Cuma',
    sat: 'Cumartesi',
    sun: 'Pazar',
  };

  const getNextDateForDay = (day: DayKey): string => {
    const map: Record<DayKey, number> = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
    const now = new Date();
    const currentDay = now.getDay();
    const target = map[day];
    const diff = (target - currentDay + 7) % 7;
    now.setDate(now.getDate() + diff);
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const subscriptionDate = getNextDateForDay(subscriptionDay);
  const subscriptionSlots = generateTimeSlots(field.id, subscriptionDate);
  const reservationPaused = Boolean(field.isReservationPaused);
  void reviewRefresh;
  const reviews = getReviewsByField(field.id);
  const ratingSummary = getFieldRatingSummary(field.id, field.rating, field.reviewCount);

  const submitReview = () => {
    if (!session || session.role !== 'customer' || !customer) {
      setReviewError('Yorum ve puan için normal kullanıcı hesabı ile giriş yapın.');
      return;
    }
    const result = upsertFieldReview({
      fieldId: field.id,
      customerId: session.userId,
      customerName: customer.fullName,
      rating: reviewRating,
      comment: reviewComment,
    });
    if (!result.ok) {
      setReviewError(result.error);
      return;
    }
    setReviewError('');
    setReviewComment('');
    setReviewRating(5);
    setReviewRefresh((v) => v + 1);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm mb-6">
          <button onClick={() => navigate('/sahalar')} className="text-gray-500 hover:text-primary-600 transition-colors">
            Sahalar
          </button>
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-gray-900 font-medium">{field.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images & Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <div className="relative">
              <ImageGallery images={field.images} fieldName={field.name} />
              <div className="absolute top-4 left-4 z-10">
                <span className="bg-primary-600 text-white text-sm font-semibold px-3 py-1.5 rounded-full shadow-md">
                  {sizeOptionsLabel}
                </span>
              </div>
              <div className="absolute top-4 right-4 z-10">
                <FavoriteButton fieldId={field.id} size="lg" />
              </div>
            </div>

            {/* Field Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">{field.name}</h1>

              {field.description && (
                <p className="text-gray-500 text-sm mb-4">{field.description}</p>
              )}

              <div className="flex items-center space-x-4 mb-4">
                <div className="flex items-center text-gray-500">
                  <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-sm">{field.address}</span>
                </div>
              </div>

              <div className="flex items-center space-x-4 mb-6">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-gray-900 font-semibold ml-1">{ratingSummary.rating}</span>
                  <span className="text-gray-500 text-sm ml-1">({ratingSummary.reviewCount} degerlendirme)</span>
                </div>
                <span className="text-gray-300">|</span>
                <span className="text-gray-500 text-sm">{field.phone}</span>
              </div>

              <h3 className="text-gray-900 font-semibold mb-3">Saha Bilgileri</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Saatlik Ücret</p>
                  <p className="text-sm font-semibold text-gray-900">{displayPrice}₺</p>
                </div>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Kaça Kaç Oynanır</p>
                  <p className="text-sm font-semibold text-gray-900">{sizeOptionsLabel}</p>
                </div>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Zemin Tipi</p>
                  <p className="text-sm font-semibold text-gray-900">{field.turfType}</p>
                </div>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Başlangıç Aralığı</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {field.slotIntervalMinutes || 60} dk
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Seans Süresi</p>
                  <p className="text-sm font-semibold text-gray-900">60 dk</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                <div className="md:col-span-2 bg-gray-50 rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Müsait Saat Aralıkları</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {selectedDayRange || field.availableTimeRanges || '09:00-24:00'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Aboneler</p>
                  <p className="text-sm font-semibold text-gray-900">{subscriberCount} kişi</p>
                </div>
              </div>

              {(field.waterPrice || field.shoeRentalPrice) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {typeof field.waterPrice === 'number' && field.waterPrice > 0 && (
                    <div className="bg-sky-50 rounded-lg border border-sky-200 p-3">
                      <p className="text-xs text-sky-700">Su Ücreti</p>
                      <p className="text-sm font-semibold text-sky-900">{field.waterPrice}₺</p>
                    </div>
                  )}
                  {typeof field.shoeRentalPrice === 'number' && field.shoeRentalPrice > 0 && (
                    <div className="bg-violet-50 rounded-lg border border-violet-200 p-3">
                      <p className="text-xs text-violet-700">Krampon Kiralama</p>
                      <p className="text-sm font-semibold text-violet-900">{field.shoeRentalPrice}₺</p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-3 mb-6">
                {field.mapsUrl && (
                  <a
                    href={field.mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white text-gray-700"
                  >
                    Google Maps'te Aç
                  </a>
                )}
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${field.lat},${field.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2 rounded-lg text-sm font-medium border border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100"
                >
                  Yol Tarifi Al
                </a>
              </div>

              {(field.paymentIban || field.paymentBankName || field.paymentAccountName || field.paymentNote) && (
                <div className="mb-6 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                  <h3 className="text-sm font-semibold text-indigo-900 mb-3">Ödeme Bilgisi</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {field.paymentBankName && (
                      <div>
                        <p className="text-indigo-700 text-xs">Banka</p>
                        <p className="text-indigo-900 font-medium">{field.paymentBankName}</p>
                      </div>
                    )}
                    {field.paymentAccountName && (
                      <div>
                        <p className="text-indigo-700 text-xs">Hesap Sahibi</p>
                        <p className="text-indigo-900 font-medium">{field.paymentAccountName}</p>
                      </div>
                    )}
                    {field.paymentIban && (
                      <div className="md:col-span-2">
                        <p className="text-indigo-700 text-xs">IBAN</p>
                        <p className="text-indigo-900 font-medium break-all">{field.paymentIban}</p>
                      </div>
                    )}
                  </div>
                  {field.paymentNote && (
                    <div className="mt-3 border-t border-indigo-200 pt-3">
                      <p className="text-indigo-700 text-xs">Saha Sahibinin Notu</p>
                      <p className="text-indigo-900 text-sm mt-1">{field.paymentNote}</p>
                    </div>
                  )}
                </div>
              )}

              {reservationPaused && (
                <div className="mb-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
                  <p className="text-sm font-semibold text-rose-700">Şuanlık rezervasyon alımı durmuştur.</p>
                  <p className="text-xs text-rose-700 mt-1">{field.reservationPausedMessage || 'Lütfen daha sonra tekrar deneyin.'}</p>
                </div>
              )}

              {/* Mini Map */}
              <div className="mb-6">
                <h3 className="text-gray-900 font-semibold mb-3">Konum</h3>
                <Suspense fallback={
                  <div className="h-[200px] bg-gray-100 rounded-xl flex items-center justify-center">
                    <p className="text-gray-400 text-sm">Harita yükleniyor...</p>
                  </div>
                }>
                  <MapView
                    fields={[field]}
                    center={{ lat: field.lat, lng: field.lng }}
                    zoom={14}
                    height="200px"
                    selectedFieldId={field.id}
                  />
                </Suspense>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Abonelik (İptal Edilene Kadar Aktif)</h4>
                {field.subscriptionEnabled ? (
                  <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                    <p className="text-xs text-emerald-800">
                      Abonelik Ücreti: <span className="font-semibold">{field.subscriptionPrice || 0}₺</span> (SS hesabına havale/EFT)
                    </p>
                    <p className="text-xs text-emerald-700 mt-1">
                      İptal edildiğinde ödenen ilk ücret iade edilir.
                    </p>
                    {field.subscriptionRules && (
                      <p className="text-xs text-emerald-700 mt-1">
                        Kural: {field.subscriptionRules}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <p className="text-xs text-gray-700">Bu sahada abonelik şu anda kapalı.</p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <select
                    value={subscriptionDay}
                    onChange={(e) => setSubscriptionDay(e.target.value as DayKey)}
                    className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    {Object.entries(dayLabels).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                  <select
                    value={subscriptionTimeLabel}
                    onChange={(e) => setSubscriptionTimeLabel(e.target.value)}
                    className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Saat seçin</option>
                    {subscriptionSlots.map((slot) => {
                      const label = slot.label || formatHourValue(slot.hour);
                      const blocked = isSlotBlockedBySubscription(field.id, subscriptionDate, label, subscriberId);
                      if (slot.isBooked || blocked) return null;
                      return <option key={`${slot.hour}-${label}`} value={label}>{label}</option>;
                    })}
                  </select>
                  <button
                    type="button"
                    disabled={!field.subscriptionEnabled}
                    onClick={() => {
                      if (!field.subscriptionEnabled) return;
                      if (!session || session.role !== 'customer') {
                        setSubscriptionError('Abonelik başlatmak için normal kullanıcı hesabı ile giriş yapın.');
                        setSubscriptionSuccess('');
                        return;
                      }
                      if (!field.subscriptionPrice || field.subscriptionPrice <= 0) {
                        setSubscriptionError('Bu saha için abonelik ücreti henüz tanımlanmamış.');
                        setSubscriptionSuccess('');
                        return;
                      }
                      if (!subscriptionTimeLabel) {
                        setSubscriptionError('Lütfen abonelik için saat seçin.');
                        setSubscriptionSuccess('');
                        return;
                      }
                      const result = createMonthlySubscription({
                        fieldId: field.id,
                        subscriberId,
                        subscriberUserId: customerSessionUserId || undefined,
                        subscriberPhone: customer?.phone,
                        dayKey: subscriptionDay,
                        timeLabel: subscriptionTimeLabel,
                        durationMinutes: 60,
                        startDate: subscriptionDate,
                        amountPaid: field.subscriptionPrice || 0,
                      });
                      if (!result.ok) {
                        setSubscriptionError(result.error);
                        setSubscriptionSuccess('');
                        return;
                      }
                      setSubscriptionError('');
                      setSubscriptionSuccess('Abonelik talebiniz SS onayına gönderildi. Onay sonrası ödeme adımı açılacaktır.');
                      setSubscriptionTimeLabel('');
                      setSubscriptionRefresh((v) => v + 1);
                    }}
                    className="rounded-lg bg-primary-600 hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-medium px-4 py-2"
                  >
                    Abonelik Talebi Gönder
                  </button>
                </div>
                {subscriptionError && (
                  <p className="text-xs text-rose-700 mt-2">{subscriptionError}</p>
                )}
                {subscriptionSuccess && (
                  <p className="text-xs text-emerald-700 mt-2">{subscriptionSuccess}</p>
                )}
                {mySubscriptions.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {mySubscriptions.map((item) => (
                      <div key={item.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        <p className="text-xs text-gray-700">
                          {dayLabels[item.dayKey]} • {item.timeLabel} • Başlangıç: {item.startDate}
                          {' • '}
                          {item.status === 'pending_owner_approval'
                            ? 'SS Onayı Bekleniyor'
                            : item.status === 'awaiting_payment'
                            ? 'Ödeme Bekleniyor'
                            : 'Aktif'}
                        </p>
                        <div className="flex items-center gap-2">
                          {item.status === 'awaiting_payment' && (
                            <button
                              type="button"
                              onClick={() => {
                                const paid = markSubscriptionPaid(item.id, subscriberId, field.subscriptionPrice || 0);
                                if (!paid.ok) {
                                  setSubscriptionError(paid.error || 'Ödeme sonrası aktivasyon başarısız.');
                                  setSubscriptionSuccess('');
                                } else {
                                  setSubscriptionSuccess('Ödeme alındı, abonelik aktif edildi.');
                                  setSubscriptionError('');
                                }
                                setSubscriptionRefresh((v) => v + 1);
                              }}
                              className="text-xs text-emerald-700 border border-emerald-200 bg-emerald-50 rounded px-2 py-1"
                            >
                              Ödemeyi Yaptım
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              const cancelled = cancelSubscription(item.id, subscriberId);
                              if (cancelled.ok) {
                                setSubscriptionSuccess(`Abonelik iptal edildi. ${cancelled.refundAmount}₺ iade edildi.`);
                                setSubscriptionError('');
                              }
                              setSubscriptionRefresh((v) => v + 1);
                            }}
                            className="text-xs text-rose-700 border border-rose-200 bg-rose-50 rounded px-2 py-1"
                          >
                            İptal
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {isOwnerOfField && fieldSubscriptions.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <h5 className="text-xs font-semibold text-gray-800 mb-2">Saha Sahibinin Abonelik Yönetimi</h5>
                    <div className="space-y-2">
                      {fieldSubscriptions.map((item) => (
                        <div key={`owner-${item.id}`} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          <p className="text-xs text-amber-900">
                            {dayLabels[item.dayKey]} • {item.timeLabel} •
                            {' '}
                            {item.status === 'pending_owner_approval'
                              ? 'Onay Bekliyor'
                              : item.status === 'awaiting_payment'
                              ? 'Ödeme Bekliyor'
                              : `Aktif • Ödeme: ${item.amountPaid}₺`}
                          </p>
                          <div className="flex items-center gap-2">
                            {item.status === 'pending_owner_approval' && (
                              <button
                                type="button"
                                onClick={() => {
                                  const approved = approveSubscriptionByOwner(item.id, field.id);
                                  if (approved.ok) {
                                    setSubscriptionSuccess('Talep onaylandı. Müşteriden ödeme bekleniyor.');
                                    setSubscriptionError('');
                                  }
                                  setSubscriptionRefresh((v) => v + 1);
                                }}
                                className="text-xs text-emerald-700 border border-emerald-200 bg-white rounded px-2 py-1"
                              >
                                Onayla
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                const cancelled = cancelSubscriptionByOwner(item.id, field.id);
                                if (cancelled.ok) {
                                  setSubscriptionSuccess(`SS iptal etti. ${cancelled.refundAmount}₺ kullanıcıya iade edildi.`);
                                  setSubscriptionError('');
                                }
                                setSubscriptionRefresh((v) => v + 1);
                              }}
                              className="text-xs text-rose-700 border border-rose-200 bg-white rounded px-2 py-1"
                            >
                              SS Olarak İptal Et
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Yorumlar ve Puanlama</h4>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm text-gray-600">Puan:</span>
                  <select
                    value={reviewRating}
                    onChange={(e) => setReviewRating(Number(e.target.value))}
                    className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    {[5, 4, 3, 2, 1].map((value) => (
                      <option key={value} value={value}>{value} Yildiz</option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  rows={3}
                  placeholder="Saha deneyiminizi yazin..."
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={submitReview}
                  className="mt-3 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-4 py-2"
                >
                  Yorum Gonder
                </button>
                {reviewError && <p className="mt-2 text-xs text-rose-700">{reviewError}</p>}
                <div className="mt-4 space-y-2">
                  {reviews.length === 0 && (
                    <p className="text-xs text-gray-500">Henüz yorum yok.</p>
                  )}
                  {reviews.map((review) => (
                    <div key={review.id} className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-gray-900">{review.customerName}</p>
                        <p className="text-xs text-yellow-600">{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}</p>
                      </div>
                      <p className="text-xs text-gray-700 mt-1">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>

              <h3 className="text-gray-900 font-semibold mb-3">Sık Sorulan Tesis Özellikleri</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                {priorityAmenityKeys.map((key) => {
                  const available = field.amenities[key];
                  const { label, icon } = AMENITY_LABELS[key];
                  return (
                    <div key={key} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span>{icon}</span>
                        <span className="text-sm text-gray-700">{label}</span>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${available ? 'bg-green-100 text-green-700' : 'bg-rose-100 text-rose-700'}`}>
                        {available ? 'Evet' : 'Hayır'}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Amenities */}
              <h3 className="text-gray-900 font-semibold mb-3">Ozellikler</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-6">
                {amenityKeys.map((key) => {
                  const available = field.amenities[key];
                  const { label, icon } = AMENITY_LABELS[key];
                  return (
                    <div
                      key={key}
                      className={`flex items-center space-x-2 px-3 py-2.5 rounded-lg border transition-colors ${
                        available
                          ? 'bg-green-50 border-green-200'
                          : 'bg-gray-50 border-gray-200 opacity-40'
                      }`}
                    >
                      <span className="text-base flex-shrink-0">{icon}</span>
                      <span
                        className={`text-sm font-medium ${
                          available
                            ? 'text-green-700'
                            : 'text-gray-400 line-through'
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>

            </div>

            {/* Weekly Calendar */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-4">Haftalik Takvim</h3>

              {/* Date Selector */}
              <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
                {weekDates.map(({ date, label, dayName }) => (
                  <button
                    key={date}
                    onClick={() => setSelectedDate(date)}
                    className={`flex-shrink-0 px-4 py-3 rounded-xl text-center transition-colors min-w-[80px] border ${
                      selectedDate === date
                        ? 'bg-primary-600 text-white border-primary-600 shadow-md'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400 hover:text-primary-600'
                    }`}
                  >
                    <div className="text-xs font-medium">{dayName}</div>
                    <div className="text-sm font-semibold mt-1">{label}</div>
                  </button>
                ))}
              </div>

              {/* Time Slots Grid */}
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {timeSlots.map((slot) => (
                  (() => {
                    const label = slot.label || formatHourValue(slot.hour);
                    const blockedBySubscription = isSlotBlockedBySubscription(field.id, selectedDate, label, subscriberId);
                    const ownedBySubscriber = isSlotOwnedBySubscriber(field.id, selectedDate, label, subscriberId);
                    const disabled = reservationPaused || slot.isBooked || blockedBySubscription;
                    return (
                  <button
                    key={`${slot.hour}-${label}`}
                    disabled={disabled}
                    onClick={() => {
                      if (!disabled) {
                        navigate(`/rezervasyon/${field.id}?date=${selectedDate}&hour=${slot.hour}`);
                      }
                    }}
                    className={`py-3 px-2 rounded-lg text-sm font-medium transition-all border ${
                      reservationPaused || slot.isBooked || blockedBySubscription
                        ? 'bg-red-50 border-red-300 text-red-500 cursor-not-allowed'
                        : 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100 hover:border-green-400 cursor-pointer'
                    }`}
                  >
                    <div>{slot.label || formatHourValue(slot.hour)}</div>
                    <div className="text-xs mt-1 opacity-75">
                      {reservationPaused ? 'Kapalı' : slot.isBooked ? 'Dolu' : blockedBySubscription ? (ownedBySubscriber ? 'Sizin Abonelik' : 'Abonelikte') : 'Musait'}
                    </div>
                  </button>
                    );
                  })()
                ))}
              </div>

              <div className="flex items-center space-x-6 mt-4 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded bg-green-50 border border-green-300" />
                  <span className="text-gray-500">Musait</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded bg-red-50 border border-red-300" />
                  <span className="text-gray-500">Dolu</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sticky top-24">
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-primary-600">{displayPrice}₺</div>
                <div className="text-gray-500">/ saat</div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Saha Boyutu</span>
                  <span className="text-gray-900 font-medium">{sizeOptionsLabel}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Zemin</span>
                  <span className="text-gray-900 font-medium">{field.turfType}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Degerlendirme</span>
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-gray-900 font-medium ml-1">{ratingSummary.rating}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Ilce</span>
                  <span className="text-gray-900 font-medium">{field.district}</span>
                </div>
              </div>

              <button
                onClick={() => {
                  if (!reservationPaused) {
                    navigate(`/rezervasyon/${field.id}?date=${selectedDate}`);
                  }
                }}
                disabled={reservationPaused}
                className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:text-gray-500 text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center space-x-2 shadow-md"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{reservationPaused ? 'Rezervasyon Kapalı' : 'Rezervasyon Yap'}</span>
              </button>

              <p className="text-gray-500 text-xs text-center mt-3">
                Takvimden musait bir saat secerek de rezervasyon yapabilirsiniz.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldDetailPage;
